/**
 * Within each column, group items into lines by y-coordinate.
 *
 * Tolerance: items whose vertical centers differ by < TOLERANCE_RATIO × line height
 * belong to the same line. We use a fraction of the line's own height (not a fixed
 * pixel value) so the algorithm is independent of the PDF's resolution / scale.
 *
 * After grouping, items within a line are sorted left-to-right by x, then deduped
 * for fake-bold / kerning-redraw artefacts.
 */
const TOLERANCE_RATIO = 0.5;
export function groupIntoLines(columns) {
    const lines = [];
    for (let colIdx = 0; colIdx < columns.length; colIdx++) {
        const col = columns[colIdx];
        if (col.items.length === 0)
            continue;
        // Sort items by y ascending (top to bottom).
        const sorted = col.items.slice().sort((a, b) => a.y - b.y);
        let currentLine = [];
        let currentYCenter = 0;
        let currentMaxHeight = 0;
        const flush = () => {
            if (currentLine.length === 0)
                return;
            const sortedByX = currentLine.slice().sort((a, b) => a.x - b.x);
            const deduped = dedupeOverlappingRedraws(sortedByX);
            const heights = deduped.map((it) => it.height).sort((a, b) => a - b);
            const median = heights[Math.floor(heights.length / 2)] ?? currentMaxHeight;
            lines.push({
                page: col.page,
                column: colIdx,
                y: currentYCenter,
                items: deduped,
                height: median,
            });
        };
        for (const it of sorted) {
            const itemCenter = it.y + it.height / 2;
            if (currentLine.length === 0) {
                currentLine = [it];
                currentYCenter = itemCenter;
                currentMaxHeight = it.height;
                continue;
            }
            const tolerance = Math.max(currentMaxHeight, it.height) * TOLERANCE_RATIO;
            if (Math.abs(itemCenter - currentYCenter) <= tolerance) {
                currentLine.push(it);
                // Running average y-center, weighted equally — keeps the line's y stable as items accumulate.
                currentYCenter = (currentYCenter * (currentLine.length - 1) + itemCenter) / currentLine.length;
                if (it.height > currentMaxHeight)
                    currentMaxHeight = it.height;
            }
            else {
                flush();
                currentLine = [it];
                currentYCenter = itemCenter;
                currentMaxHeight = it.height;
            }
        }
        flush();
    }
    return lines;
}
/**
 * Within an x-sorted line, remove items that are kerning/fake-bold redraws of
 * previous items. Two patterns:
 *
 *   (A) Full duplicate: cur.str === prev.str AND cur.x overlaps prev's left half.
 *   (B) Trailing single-char redraw: cur is one character matching prev's last char,
 *       at an x position inside or near prev's right edge. This is the HBS/PowerPoint
 *       case where each word's last visible glyph is restruck for visual weight.
 *
 * When we drop a redraw, we propagate any bold/italic flag from the dropped item
 * onto the preserved one (because the redraw layer often carries the bold styling).
 */
function dedupeOverlappingRedraws(sortedByX) {
    if (sortedByX.length <= 1)
        return sortedByX;
    const out = [sortedByX[0]];
    for (let i = 1; i < sortedByX.length; i++) {
        const cur = sortedByX[i];
        const prev = out[out.length - 1];
        // (A) Full-string duplicate within the same line.
        const fullDup = prev.str === cur.str &&
            cur.x < prev.x + (prev.xEnd - prev.x) * 0.5;
        // (B) Trailing single-char redraw.
        const tailRedraw = cur.str.length === 1 &&
            prev.str.endsWith(cur.str) &&
            cur.x >= prev.xEnd - prev.height * 1.5 &&
            cur.x < prev.xEnd + prev.height * 0.2;
        if (fullDup || tailRedraw) {
            if (cur.bold)
                prev.bold = true;
            if (cur.italic)
                prev.italic = true;
            continue;
        }
        // (C) Multi-char redraw: cur's first character is a redraw of prev's last,
        // followed by genuinely new content (e.g., prev = "mpany", cur = "y (A)").
        // We trim cur's leading duplicate character.
        if (cur.str.length > 1 &&
            prev.str.length > 0 &&
            prev.str.endsWith(cur.str.charAt(0)) &&
            cur.x >= prev.xEnd - prev.height * 1.5 &&
            cur.x < prev.xEnd) {
            if (cur.bold)
                prev.bold = true;
            if (cur.italic)
                prev.italic = true;
            out.push({ ...cur, str: cur.str.slice(1), x: prev.xEnd });
            continue;
        }
        out.push(cur);
    }
    return out;
}
//# sourceMappingURL=lines.js.map